import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { TextField } from '../ui/TextField';
import { TextArea } from '../ui/TextArea';
import { Badge } from '../ui/badge';
import { 
  FeatherX,
  FeatherSave,
  FeatherPhone,
  FeatherMail,
  FeatherMessageSquare,
  FeatherUsers,
  FeatherCalendar,
  FeatherClock,
  FeatherCheck,
  FeatherAlertCircle,
  FeatherUser,
  FeatherGlobe
} from '../ui/icons';
import api from '../../services/api';
import './ContactAttemptForm.css';

const ContactAttemptForm = ({ 
  partner, 
  onSuccess, 
  onCancel, 
  initialData = null,
  isEdit = false 
}) => {
  const [formData, setFormData] = useState({
    contact_method: 'phone',
    contact_person_name: '',
    contact_person_role: '',
    contact_person_email: '',
    contact_person_phone: '',
    subject: '',
    content: '',
    status: 'sent',
    outcome: 'pending_response',
    follow_up_required: false,
    follow_up_date: '',
    follow_up_notes: '',
    notes: ''
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Contact method options
  const contactMethods = [
    { value: 'phone', label: 'Phone Call', icon: <FeatherPhone size={16} /> },
    { value: 'email', label: 'Email', icon: <FeatherMail size={16} /> },
    { value: 'linkedin', label: 'LinkedIn', icon: <FeatherGlobe size={16} /> },
    { value: 'whatsapp', label: 'WhatsApp', icon: <FeatherMessageSquare size={16} /> },
    { value: 'meeting', label: 'Meeting', icon: <FeatherUsers size={16} /> },
    { value: 'other', label: 'Other', icon: <FeatherUser size={16} /> }
  ];

  // Status options based on contact method
  const getStatusOptions = (method) => {
    const baseStatuses = [
      { value: 'sent', label: 'Sent/Initiated' },
      { value: 'no_response', label: 'No Response' },
      { value: 'responded', label: 'Responded' },
      { value: 'interested', label: 'Interested' },
      { value: 'not_interested', label: 'Not Interested' }
    ];

    if (method === 'phone') {
      return [
        ...baseStatuses,
        { value: 'busy', label: 'Busy' },
        { value: 'call_back_later', label: 'Call Back Later' },
        { value: 'wrong_number', label: 'Wrong Number' }
      ];
    } else if (method === 'email') {
      return [
        { value: 'sent', label: 'Sent' },
        { value: 'delivered', label: 'Delivered' },
        { value: 'opened', label: 'Opened' },
        { value: 'clicked', label: 'Clicked' },
        { value: 'responded', label: 'Responded' },
        { value: 'bounced', label: 'Bounced' },
        { value: 'no_response', label: 'No Response' },
        { value: 'interested', label: 'Interested' },
        { value: 'not_interested', label: 'Not Interested' }
      ];
    }

    return baseStatuses;
  };

  // Outcome options
  const outcomeOptions = [
    { value: 'successful', label: 'Successful', color: 'green' },
    { value: 'unsuccessful', label: 'Unsuccessful', color: 'red' },
    { value: 'needs_follow_up', label: 'Needs Follow-up', color: 'orange' },
    { value: 'pending_response', label: 'Pending Response', color: 'blue' }
  ];

  useEffect(() => {
    if (partner) {
      // Pre-fill contact person info from partner data
      const primaryContact = partner.primary_contact || {};
      const decisionMaker = partner.decision_maker || {};
      
      setFormData(prev => ({
        ...prev,
        contact_person_name: primaryContact.name || decisionMaker.name || '',
        contact_person_role: primaryContact.role || decisionMaker.role || '',
        contact_person_email: primaryContact.email || decisionMaker.email || partner.email || '',
        contact_person_phone: primaryContact.phone || partner.phone || ''
      }));
    }

    if (initialData && isEdit) {
      setFormData(prev => ({
        ...prev,
        ...initialData,
        follow_up_date: initialData.follow_up_date ? 
          new Date(initialData.follow_up_date).toISOString().slice(0, 16) : ''
      }));
    }
  }, [partner, initialData, isEdit]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Auto-update status based on contact method
    if (field === 'contact_method') {
      setFormData(prev => ({
        ...prev,
        [field]: value,
        status: 'sent'
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const submitData = {
        ...formData,
        partner_id: partner.id,
        follow_up_date: formData.follow_up_required && formData.follow_up_date ? 
          new Date(formData.follow_up_date).toISOString() : null
      };

      let response;
      if (isEdit && initialData) {
        response = await api.put(`/api/contact-attempts/${initialData.id}`, submitData);
      } else {
        response = await api.post('/api/contact-attempts', submitData);
      }

      if (onSuccess) {
        onSuccess(response.data.attempt || response.data);
      }
    } catch (err) {
      console.error('Error submitting contact attempt:', err);
      setError(err.response?.data?.error || 'Failed to save contact attempt');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const statusColors = {
      sent: 'blue',
      delivered: 'blue',
      opened: 'blue',
      clicked: 'blue',
      responded: 'green',
      interested: 'green',
      not_interested: 'red',
      no_response: 'orange',
      busy: 'orange',
      call_back_later: 'orange',
      wrong_number: 'red',
      bounced: 'red',
      failed: 'red'
    };
    return statusColors[status] || 'gray';
  };

  const getOutcomeColor = (outcome) => {
    const outcomeColors = {
      successful: 'green',
      unsuccessful: 'red',
      needs_follow_up: 'orange',
      pending_response: 'blue'
    };
    return outcomeColors[outcome] || 'gray';
  };

  return (
    <div className="contact-attempt-form">
      <div className="form-header">
        <h2>{isEdit ? 'Edit Contact Attempt' : 'Log Contact Attempt'}</h2>
        <Button
          variant="neutral-tertiary"
          icon={<FeatherX />}
          onClick={onCancel}
        />
      </div>

      {error && (
        <div className="error-message">
          <FeatherAlertCircle size={16} />
          <span>{error}</span>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="form-sections">
          
          {/* Contact Method Section */}
          <div className="form-section">
            <h3>Contact Method</h3>
            <div className="contact-method-grid">
              {contactMethods.map(method => (
                <div
                  key={method.value}
                  className={`contact-method-option ${formData.contact_method === method.value ? 'selected' : ''}`}
                  onClick={() => handleInputChange('contact_method', method.value)}
                >
                  {method.icon}
                  <span>{method.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Contact Person Section */}
          <div className="form-section">
            <h3>Contact Person</h3>
            <div className="form-grid">
              <TextField label="Name">
                <TextField.Input
                  value={formData.contact_person_name}
                  onChange={(e) => handleInputChange('contact_person_name', e.target.value)}
                  placeholder="Contact person name"
                />
              </TextField>
              
              <TextField label="Role">
                <TextField.Input
                  value={formData.contact_person_role}
                  onChange={(e) => handleInputChange('contact_person_role', e.target.value)}
                  placeholder="e.g., Project Manager, CTO"
                />
              </TextField>
              
              <TextField label="Email">
                <TextField.Input
                  type="email"
                  value={formData.contact_person_email}
                  onChange={(e) => handleInputChange('contact_person_email', e.target.value)}
                  placeholder="contact@company.com"
                />
              </TextField>
              
              <TextField label="Phone">
                <TextField.Input
                  value={formData.contact_person_phone}
                  onChange={(e) => handleInputChange('contact_person_phone', e.target.value)}
                  placeholder="+1 (555) 123-4567"
                />
              </TextField>
            </div>
          </div>

          {/* Contact Details Section */}
          <div className="form-section">
            <h3>Contact Details</h3>
            <div className="form-grid">
              {formData.contact_method === 'email' && (
                <TextField label="Subject" className="full-width">
                  <TextField.Input
                    value={formData.subject}
                    onChange={(e) => handleInputChange('subject', e.target.value)}
                    placeholder="Email subject"
                  />
                </TextField>
              )}
              
              <div className="full-width">
                <TextArea label="Content/Message">
                  <TextArea.Input
                    value={formData.content}
                    onChange={(e) => handleInputChange('content', e.target.value)}
                    placeholder={
                      formData.contact_method === 'email' 
                        ? 'Email content...' 
                        : formData.contact_method === 'phone'
                        ? 'What was discussed during the call...'
                        : 'Details of the contact...'
                    }
                    rows={4}
                  />
                </TextArea>
              </div>
            </div>
          </div>

          {/* Status & Outcome Section */}
          <div className="form-section">
            <h3>Status & Outcome</h3>
            <div className="form-grid">
              <div className="form-group">
                <label>Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => handleInputChange('status', e.target.value)}
                  className="form-select"
                >
                  {getStatusOptions(formData.contact_method).map(status => (
                    <option key={status.value} value={status.value}>
                      {status.label}
                    </option>
                  ))}
                </select>
                <Badge 
                  variant="neutral-secondary" 
                  style={{ marginTop: '4px', backgroundColor: `var(--color-${getStatusColor(formData.status)}-100)`, color: `var(--color-${getStatusColor(formData.status)}-700)` }}
                >
                  {getStatusOptions(formData.contact_method).find(s => s.value === formData.status)?.label}
                </Badge>
              </div>
              
              <div className="form-group">
                <label>Outcome</label>
                <select
                  value={formData.outcome}
                  onChange={(e) => handleInputChange('outcome', e.target.value)}
                  className="form-select"
                >
                  {outcomeOptions.map(outcome => (
                    <option key={outcome.value} value={outcome.value}>
                      {outcome.label}
                    </option>
                  ))}
                </select>
                <Badge 
                  variant="neutral-secondary" 
                  style={{ marginTop: '4px', backgroundColor: `var(--color-${getOutcomeColor(formData.outcome)}-100)`, color: `var(--color-${getOutcomeColor(formData.outcome)}-700)` }}
                >
                  {outcomeOptions.find(o => o.value === formData.outcome)?.label}
                </Badge>
              </div>
            </div>
          </div>

          {/* Follow-up Section */}
          <div className="form-section">
            <h3>Follow-up</h3>
            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.follow_up_required}
                  onChange={(e) => handleInputChange('follow_up_required', e.target.checked)}
                />
                Follow-up required
              </label>
            </div>
            
            {formData.follow_up_required && (
              <div className="form-grid">
                <TextField label="Follow-up Date & Time">
                  <TextField.Input
                    type="datetime-local"
                    value={formData.follow_up_date}
                    onChange={(e) => handleInputChange('follow_up_date', e.target.value)}
                  />
                </TextField>
                
                <div className="full-width">
                  <TextArea label="Follow-up Notes">
                    <TextArea.Input
                      value={formData.follow_up_notes}
                      onChange={(e) => handleInputChange('follow_up_notes', e.target.value)}
                      placeholder="Notes for the follow-up..."
                      rows={3}
                    />
                  </TextArea>
                </div>
              </div>
            )}
          </div>

          {/* Additional Notes Section */}
          <div className="form-section">
            <h3>Additional Notes</h3>
            <TextArea label="Notes">
              <TextArea.Input
                value={formData.notes}
                onChange={(e) => handleInputChange('notes', e.target.value)}
                placeholder="Any additional notes or observations..."
                rows={3}
              />
            </TextArea>
          </div>
        </div>

        <div className="form-actions">
          <Button
            type="button"
            variant="neutral-tertiary"
            onClick={onCancel}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="brand-primary"
            icon={<FeatherSave />}
            loading={loading}
            disabled={loading}
          >
            {loading ? (isEdit ? 'Updating...' : 'Saving...') : (isEdit ? 'Update Attempt' : 'Log Attempt')}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default ContactAttemptForm; 